const API_BASE = `${import.meta.env.VITE_API_URL ?? ''}/api/v1`;
const DEVICE_ID_KEY = 'compras-device-id';
export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}
class ApiClient {
  private accessToken: string | null = null;
  private deviceId: string;
  private refreshing: Promise<void> | null = null;
  constructor() {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    this.deviceId = id;
  }
  setAccessToken(token: string | null) {
    this.accessToken = token;
  }
  getDeviceId(): string {
    return this.deviceId;
  }
  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: { skipAuth?: boolean; isRetry?: boolean } = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Device-Id': this.deviceId,
    };
    if (!options.skipAuth && this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });
    // 401 con token: intentar refresh una vez
    if (response.status === 401 && this.accessToken && !options.isRetry && !options.skipAuth) {
      try {
        await this.refresh();
        return this.request<T>(method, path, body, { ...options, isRetry: true });
      } catch {
        this.accessToken = null;
        window.dispatchEvent(new CustomEvent('auth:expired'));
        throw new ApiError('TOKEN_EXPIRED', 'Sesión expirada', 401);
      }
    }
    let json: any;
    try {
      json = await response.json();
    } catch {
      throw new ApiError('NETWORK_ERROR', 'Error de red', response.status);
    }
    if (!json.success) {
      throw new ApiError(
        json.error?.code ?? 'UNKNOWN_ERROR',
        json.error?.message ?? 'Error desconocido',
        response.status,
      );
    }
    return json.data;
  }
  private async refresh(): Promise<void> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Device-Id': this.deviceId },
      });
      if (!response.ok) throw new Error('Refresh failed');
      const json = await response.json();
      if (!json.success) throw new Error('Refresh failed');
      this.accessToken = json.data.accessToken;
    })().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }
}

export const api = new ApiClient();
